import { animated, useSpring } from '@react-spring/web';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import CloseIcon from '@mui/icons-material/Close';

interface DraggableBottomSheetModalProps {
    open: boolean;
    onDismiss: () => void;
    children: JSX.Element;
    height?: string;
    style?: React.CSSProperties;
    customHeight?: string;
    hideCloseIcon?: boolean;
    minHeight?: number;
    maxHeight?: number;
}

const DraggableBottomSheetModal = (
    {
        open,
        onDismiss,
        children,
        style,
        customHeight,
        hideCloseIcon,
        minHeight = 200,
        maxHeight = window.innerHeight * 0.9,
    }: DraggableBottomSheetModalProps,
    ref: any,
) => {
    const { themeStyle } = useThemeContext();
    const [isDragging, setIsDragging] = useState(false);
    const [currentHeight, setCurrentHeight] = useState(
        customHeight
            ? customHeight.includes('%')
                ? (window.innerHeight * parseInt(customHeight)) / 100
                : parseInt(customHeight)
            : window.innerHeight * 0.6,
    );
    const [startY, setStartY] = useState(0);
    const [startHeight, setStartHeight] = useState(currentHeight);
    const containerRef = useRef<HTMLDivElement>(null);

    const { height, opacity } = useSpring({
        height: open ? currentHeight : 0,
        bottom: open ? 0 : -currentHeight,
        opacity: open ? 1 : 0,
        config: { tension: 300, friction: 30 },
    });

    // Handle drag start (both mouse and touch)
    const handleDragStart = useCallback(
        (clientY: number) => {
            setIsDragging(true);
            setStartY(clientY);
            setStartHeight(currentHeight);
            document.body.style.userSelect = 'none'; // Prevent text selection
        },
        [currentHeight],
    );

    // Handle drag move (both mouse and touch)
    const handleDragMove = useCallback(
        (clientY: number) => {
            if (!isDragging) return;

            const deltaY = startY - clientY; // Positive when dragging up, negative when dragging down
            const newHeight = Math.max(minHeight, Math.min(maxHeight, startHeight + deltaY));

            setCurrentHeight(newHeight);

            // If dragged down significantly below min height, trigger dismiss
            if (startHeight + deltaY < minHeight - 100) {
                onDismiss();
            }
        },
        [isDragging, startY, startHeight, minHeight, maxHeight, onDismiss],
    );

    // Handle drag end
    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
        document.body.style.userSelect = ''; // Restore text selection

        // Snap to min height if too small
        if (currentHeight < minHeight + 50) {
            setCurrentHeight(minHeight);
        }
    }, [currentHeight, minHeight]);

    // Mouse event handlers
    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            handleDragStart(e.clientY);
        },
        [handleDragStart],
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            handleDragMove(e.clientY);
        },
        [handleDragMove],
    );

    const handleMouseUp = useCallback(() => {
        handleDragEnd();
    }, [handleDragEnd]);

    // Touch event handlers
    const handleTouchStart = useCallback(
        (e: React.TouchEvent) => {
            e.preventDefault();
            const touch = e.touches[0];
            handleDragStart(touch.clientY);
        },
        [handleDragStart],
    );

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            e.preventDefault();
            const touch = e.touches[0];
            handleDragMove(touch.clientY);
        },
        [handleDragMove],
    );

    const handleTouchEnd = useCallback(() => {
        handleDragEnd();
    }, [handleDragEnd]);

    // Add global event listeners when dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('touchend', handleTouchEnd);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    // Reset height when opening
    useEffect(() => {
        if (open) {
            const initialHeight = customHeight
                ? customHeight.includes('%')
                    ? (window.innerHeight * parseInt(customHeight)) / 100
                    : parseInt(customHeight)
                : window.innerHeight * 0.6;
            setCurrentHeight(initialHeight);
        }
    }, [open, customHeight]);

    if (!open) return null;

    return (
        <animated.div
            ref={containerRef}
            style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1500,
                height,
                opacity,
                backgroundColor: theme[themeStyle].accountGray,
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                ...style,
            }}>
            {/* Drag Handle */}
            <Box
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                sx={{
                    width: '100%',
                    height: 32,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    touchAction: 'none',
                    py: 1,
                    '&:active': {
                        cursor: 'grabbing',
                    },
                    '&:hover .drag-handle': {
                        backgroundColor: theme[themeStyle].black,
                        transform: 'scaleY(1.2)',
                    },
                }}>
                <Box
                    className="drag-handle"
                    sx={{
                        width: 50,
                        height: 5,
                        backgroundColor: theme[themeStyle].handle || theme[themeStyle].placeholderGray,
                        borderRadius: 3,
                        transition: 'all 0.2s ease',
                        boxShadow: `0 2px 8px rgba(0, 0, 0, 0.3)`,
                        border: `1px solid ${
                            themeStyle === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                        }`,
                    }}
                />
            </Box>

            {/* Close Icon */}
            {!hideCloseIcon && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 15,
                        zIndex: 10,
                        cursor: 'pointer',
                    }}
                    onClick={onDismiss}>
                    <CloseIcon sx={{ color: theme[themeStyle].tag_white }} />
                </Box>
            )}

            {/* Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{children}</Box>
        </animated.div>
    );
};

const Wrapped = React.forwardRef(DraggableBottomSheetModal);

export { Wrapped as DraggableBottomSheetModal };
